import { createPersonLegal } from '@dealstack/frontend/app/services/Person.service';
import {
	GENERAL_DEAL_INPUTS_FORM,
	IMipCreateGeneralDealInputsValues,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/GeneralDealInputs/useGeneralDealInputsController';
import { useMipEntitySelectController } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/GeneralDealInputs/useMipEntitySelectController';
import {
	addMipProgrammeEntity,
	MipProgrammeEntity,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { CreatableSelect } from '@dealstack/frontend/shared/components/forms/CreatableSelect/CreatableSelect';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { CurrencyType } from '@dealstack/shared/lib/generated';
import { useMutation } from '@tanstack/react-query';
import { Button, Drawer, Flex, FlexProps, Text } from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
	getProgrammeEntitySelectOptions,
	shouldCreateProgrammeEntityOption,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeGeneralSettings/ProgrammeEntitiesSection.utils';

interface Props {
	dealId: string;
	opened: boolean;
	programmeEntities: MipProgrammeEntity[];
	onAdded: () => Promise<void>;
	onClose: () => void;
}

export const ProgrammeEntityAddDrawer = ({
	dealId,
	opened,
	programmeEntities,
	onAdded,
	onClose,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const addEntityForm = useForm<IMipCreateGeneralDealInputsValues>({
		defaultValues: {
			[GENERAL_DEAL_INPUTS_FORM.ENTITY_ID]: null,
			[GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME]: '',
			[GENERAL_DEAL_INPUTS_FORM.INVESTOR_INSTRUCTIONS]: '',
			[GENERAL_DEAL_INPUTS_FORM.MIP_CURRENCY]: CurrencyType.EUR,
		},
	});
	const selectedEntityId = addEntityForm.watch(GENERAL_DEAL_INPUTS_FORM.ENTITY_ID);
	const { entityOptions, onCreateNewEntity, selectedEntity, refetchEntities } =
		useMipEntitySelectController(dealId, addEntityForm);

	const handleClose = () => {
		addEntityForm.reset();
		onClose();
	};

	const { mutateAsync: addEntity, isPending: isAddingEntity } = useMutation({
		mutationFn: async () => {
			if (!selectedEntity) {
				return;
			}

			const entityId = selectedEntity.isNew
				? (
						await createPersonLegal({
							dealId,
							createArgs: { name: selectedEntity.label },
						})
					).id
				: selectedEntity.value;

			await addMipProgrammeEntity(entityId);
		},
		onSuccess: async () => {
			await refetchEntities();
			showToastV2({
				title: tMipManage('programme_entities.add_drawer.toast_added', {
					defaultValue: 'Entity added to programme',
				}),
				state: 'success',
			});
			addEntityForm.reset();
			onClose();
			await onAdded();
		},
		onError: () => {
			showToastV2({
				title: tMipManage('programme_entities.add_drawer.toast_add_failed', {
					defaultValue: 'Failed to add entity to programme',
				}),
				state: 'error',
			});
		},
	});

	const entitySelectOptions = getProgrammeEntitySelectOptions(entityOptions, programmeEntities);

	return (
		<Drawer
			opened={opened}
			onClose={handleClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tMipManage('programme_entities.add_drawer.title', {
				defaultValue: 'Add entity to programme',
			})}
			trapFocus={false}
		>
			<DrawerContent direction="column" justify="space-between" h="100%">
				<Flex direction="column" gap="1rem">
					<Text size="sm" color="dimmed">
						{tMipManage('programme_entities.add_drawer.description', {
							defaultValue:
								'Select an entity from your Deal Directory to affiliate with this investment programme.',
						})}
					</Text>
					<Controller
						name={GENERAL_DEAL_INPUTS_FORM.ENTITY_ID}
						control={addEntityForm.control}
						render={({ field }) => (
							<CreatableSelect
								value={field.value ?? null}
								onChange={field.onChange}
								label={tMipManage('programme_entities.add_drawer.entity_label', {
									defaultValue: 'Add entity',
								})}
								placeholder={tMipManage(
									'programme_entities.add_drawer.entity_placeholder',
									{ defaultValue: 'Search existing entity' }
								)}
								w="100%"
								shouldCreate={(query) =>
									shouldCreateProgrammeEntityOption(
										query,
										entityOptions,
										programmeEntities
									)
								}
								onCreate={onCreateNewEntity}
								getCreateLabel={(query) => (
									<Flex align="center" gap="0.5rem">
										<Icon variant="add" />
										<Text>
											{tMipManage(
												'programme_entities.add_drawer.create_new_entity',
												{ defaultValue: 'Create new entity' }
											)}
										</Text>
										<Text>"{query}"</Text>
									</Flex>
								)}
								data={entitySelectOptions}
							/>
						)}
					/>
				</Flex>

				<Button
					fullWidth
					disabled={!selectedEntityId || isAddingEntity}
					onClick={() => void addEntity()}
				>
					{tMipManage('programme_entities.add_drawer.submit', { defaultValue: 'Add' })}
				</Button>
			</DrawerContent>
		</Drawer>
	);
};

const DrawerContent = styled(Flex)<FlexProps>`
	padding-bottom: 0.25rem;
`;
